import axios from 'axios';

export const provisionSmsAccount = async () => {
    try {
        return await axios
            .post('/app/smsBroadcast/provisionAccount');
    } catch (error) {
        throw new Error('Could not provision an SMS account');
    }
};

export const getSmsAccount = async () => {
    try {
        const { data: smsAccount } = await axios
            .get('/app/smsBroadcast/getAccount');

        return smsAccount;
    } catch (error) {
        throw new Error('Could not get SMS account');
    }
};

export const getSmsAccountVerification = async (phoneNumber) => {
    try {
        if (!phoneNumber) {
            throw new Error();
        }

        const { data } = await axios
            .get(`/app/automatedSmsFunnel/v1/verificationStatus/${phoneNumber}`);

        return data || {};
    } catch (error) {
        throw new Error('Could not get SMS account verification status');
    }
};

export const getUserList = () => {
    return axios
        .get('/app/smsBroadcast/userList');
};

export const getBroadcastOptions = () => {
    return axios
        .get('/app/broadcast/broadcastOptions');
};

export const updateSmsAccount = async ({
    userId,
    companyName,
    termsAndConditionsTimestamp,
    termsAndConditionsVersion,
    optInMessageIntro,
    replyMessage,
    status,
}) => {
    const params = {
        userId,
        companyName,
        termsAndConditionsTimestamp,
        termsAndConditionsVersion,
        optInMessageIntro,
        replyMessage,
        status,
    };

    try {
        return await axios
            .put('/app/smsBroadcast/updateAccount', params);
    } catch (error) {
        throw new Error('Could not update SMS account');
    }
};

export const createSmsBroadcast = async ({
    messageContent = '',
    recipientCriteria = '[]',
    scheduleDate = '',
    title = '',
    status = 'DRAFT',
    createdBy,
    schedule = {
        timing: 'sendNow',
        timingDetails: {},
    },
    phoneTypes = [],
    phoneFields = [],
}) => {
    const params = {
        recipientCriteria: JSON.stringify(recipientCriteria),
        title,
        messageContent,
        scheduleDate,
        status,
        createdBy,
        phoneTypes,
        phoneFields,
    };

    try {
        const { data: createdSmsBroadcast } = await axios
            .post('/app/smsBroadcast/', params);

        const { recipientCriteria: criteria } = createdSmsBroadcast;

        createdSmsBroadcast.recipientCriteria = JSON.parse(criteria) || [];
        // schedule is not part of the endpoint response; keep property in created broadcast
        createdSmsBroadcast.schedule = schedule;

        return createdSmsBroadcast;
    } catch (error) {
        throw new Error('Could not create a new SMS broadcast');
    }
};

export const getSmsBroadcast = async (id) => {
    try {
        const { data: smsBroadcast } = await axios.get(`/app/smsBroadcast/${id}`);

        const {
            recipientCriteria: criteria,
            messageContent: content,
            phoneTypes,
            phoneFields,
        } = smsBroadcast;

        smsBroadcast.recipientCriteria = JSON.parse(criteria) || [];

        smsBroadcast.messageContent = content || '';

        smsBroadcast.phoneTypes = phoneTypes || [];

        smsBroadcast.phoneFields = phoneFields || [];

        const schedule = {
            timing: 'sendNow',
            timingDetails: {},
        };

        smsBroadcast.schedule = schedule;

        return smsBroadcast;
    } catch (error) {
        throw new Error(`Could not get SMS broadcast with ID ${id}`);
    }
};

export const getSmsBroadcasts = async ({
    completionStatus = null,
    pageNumber = 0,
    limit = 10,
    search = '',
}) => {
    const params = {
        completionStatus,
        pageNumber,
        limit,
        search,
    };

    try {
        const { data: smsBroadcasts } = await axios
            .post('/app/smsBroadcast/list/', params);

        return smsBroadcasts;
    } catch (error) {
        throw new Error('Could not retrieve SMS broadcasts');
    }
};

export const updateSmsBroadcast = async ({
    id,
    messageContent = '',
    recipientCriteria = '[]',
    scheduleDate = '',
    title = '',
    status = 'DRAFT',
    updatedBy,
    schedule = {
        timing: 'sendNow',
        timingDetails: {},
    },
    phoneTypes = [],
    phoneFields = [],
}) => {
    const params = {
        messageContent,
        recipientCriteria: JSON.stringify(recipientCriteria),
        scheduleDate,
        title,
        status,
        updatedBy,
        phoneTypes,
        phoneFields,
    };

    try {
        const { data: updatedSmsBroadcast } = await axios
            .put(`/app/smsBroadcast/${id}`, params);

        const { recipientCriteria: criteria } = updatedSmsBroadcast;

        updatedSmsBroadcast.recipientCriteria = JSON.parse(criteria) || [];
        // schedule is not part of the endpoint response; keep property in updated broadcast
        updatedSmsBroadcast.schedule = schedule;

        return updatedSmsBroadcast;
    } catch (error) {
        throw new Error(`Could not update SMS broadcast with ID ${id}`);
    }
};

export const deleteSmsBroadcast = (id) => {
    return axios
        .delete(`/app/smsBroadcast/${id}`);
};

export const scheduleSmsBroadcast = async ({
    id,
    recipients = [],
    scheduleDate = '',
    updatedBy,
}) => {
    const params = {
        recipients,
        scheduleDate,
        updatedBy,
    };

    try {
        const { data: scheduledSmsBroadcast } = await axios.post(`/app/smsBroadcast/${id}/schedule/`, params);

        const { recipientCriteria: criteria } = scheduledSmsBroadcast;

        scheduledSmsBroadcast.recipientCriteria = JSON.parse(criteria) || [];

        return scheduledSmsBroadcast;
    } catch (error) {
        throw new Error(`Could not schedule SMS broadcast with ID ${id}`);
    }
};

export const stopSmsBroadcast = async ({
    id,
    updatedBy,
}) => {
    const params = {
        updatedBy,
    };

    try {
        return await axios
            .post(`/app/smsBroadcast/${id}/stop/`, params);
    } catch (error) {
        throw new Error(`Could not stop SMS broadcast with ID ${id}`);
    }
};

export const resumeSmsBroadcast = async ({
    id,
    updatedBy,
}) => {
    const params = {
        updatedBy,
    };

    try {
        const { data: resumedBroadcast } = await axios
            .post(`/app/smsBroadcast/${id}/resume/`, params);

        return resumedBroadcast;
    } catch (error) {
        throw new Error(`Could not resume SMS broadcast with ID ${id}`);
    }
};

export const getSmsBroadcastStatistics = (id) => {
    return axios
        .get(`/app/smsBroadcast/${id}/statistics/`);
};

export const searchRecipients = async ({
    query,
    limit = 100,
    offset = 0,
}) => {
    const params = {
        q: query,
        limit,
        offset,
    };

    try {
        const { data: recipients } = await axios
            .get('/app/smsBroadcast/recipientsQuery', { params });

        return recipients;
    } catch (error) {
        throw new Error(`Could not fetch list of recipients for query ${query}`);
    }
};

export const getRecipientPhoneStatus = async ({
    fromNumber,
    phoneNumbers = '',
}) => {
    const params = {
        fromNumber,
        phoneNumbers,
    };

    try {
        const { data: optInStatuses } = await axios.post('/app/smsBroadcast/phoneStatus/', params);

        return optInStatuses;
    } catch (error) {
        throw new Error('Could not get recipient phone statuses');
    }
};

export const getRecipientList = async ({
    savedSearch = '',
    tag = '',
    singleContactTag = '',
    savedSearchPageNumber = 0,
    tagGroupNextContact = 0,
}) => {
    const params = {
        saved_search: savedSearch,
        tag,
        single_contact_tag: singleContactTag,
        saved_search_page_number: savedSearchPageNumber,
        tag_group_next_contact: tagGroupNextContact,
    };

    try {
        const { data: recipientList } = await axios.get('/app/smsBroadcast/recipientList/', { params });

        return recipientList;
    } catch (error) {
        throw new Error('Could not get recipient list');
    }
};

export const getLocalizationInfo = async () => {
    try {
        const { data: localizationInfo } = await axios.get('/app/smsBroadcast/localizationInfo');

        return localizationInfo;
    } catch (error) {
        throw new Error('Could not get localization info');
    }
};
